/**
 * Persist adapter for host-owned draft data.
 *
 * Keys are `flow-data-{namespace}` in localStorage. Existing namespaces are frozen:
 * `kit-registration`, `sample-collection` — renaming strands live patient drafts.
 */
export const FLOW_DATA_STORAGE_PREFIX = 'flow-data-'

export type PersistAdapter = {
  read: (namespace: string) => unknown
  write: (namespace: string, value: unknown) => void
  clear: (namespace: string) => void
  clearAll: () => void
}

function keyForNamespace(namespace: string): string | null {
  if (!namespace) return null
  const key = `${FLOW_DATA_STORAGE_PREFIX}${namespace}`
  return key.startsWith(FLOW_DATA_STORAGE_PREFIX) ? key : null
}

export const persistAdapter: PersistAdapter = {
  read(namespace) {
    if (typeof window === 'undefined') return null

    const key = keyForNamespace(namespace)
    if (!key) return null

    try {
      const stored = localStorage.getItem(key)
      return stored ? JSON.parse(stored) : null
    } catch {
      return null
    }
  },

  write(namespace, value) {
    if (typeof window === 'undefined') return

    const key = keyForNamespace(namespace)
    if (!key) return

    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch (error) {
      console.warn('Error persisting flow data:', error)
    }
  },

  clear(namespace) {
    if (typeof window === 'undefined') return

    const key = keyForNamespace(namespace)
    if (!key) return

    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.warn('Error clearing flow data:', error)
    }
  },

  clearAll() {
    if (typeof window === 'undefined') return

    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith(FLOW_DATA_STORAGE_PREFIX))
        .forEach((key) => {
          try {
            localStorage.removeItem(key)
          } catch (error) {
            console.warn('Error clearing flow data:', error)
          }
        })
    } catch (error) {
      console.warn('Error clearing flow data:', error)
    }
  },
}
